/**
 * A branch is a set of copies (RFC 0313 3j to 3l).
 *
 * PROVISIONAL, as the RFC says: the local half of the branching proof passed;
 * the relay, fresh-device and VM half has not run.
 *
 * READS AT THE BASE. A branch pins every document's heads when it opens, so an
 * app's view, skill and records are read from one moment and never mixed with
 * main's later work. A document nobody on the branch has written reads at
 * those heads; the first write forks it there (`A.clone(A.view(doc, heads))`),
 * so the copy shares main's history and merging it back is a plain Automerge
 * merge. Main never reads a copy.
 *
 * A BRANCH FROM A VERSION reads at the version's heads too. Its first write
 * copies main as it is now and writes a restore to the version's heads into the
 * copy, so merging it back is how the past comes back (3j, 3c).
 *
 * TWO WAYS OUT. Discard marks the branch discarded and nothing else. Merge
 * saves a version of main first, merges each copy with a progress record so a
 * retry resumes rather than repeats, and refuses while a paragraph or field was
 * changed on both sides and nobody has picked.
 */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { Automerge as A } from '@automerge/automerge-repo/slim'
import { diffData, type FieldChange } from '../documents/history'
import {
  catalogDocument,
  createDocument,
  deleteDocument,
  touchCatalog,
} from '../documents/mutations'
import { projectDocuments, readDocumentContent } from '../documents/reads'
import { changeProject, projectString } from '../project/scalars'
import { changeNote } from '../session/agent-edits'
import {
  diffParagraphs,
  type Hunk,
  mergeParagraphs,
  type Overlap,
  type Pick,
} from './paragraphs'
import { listVersions, saveVersion } from './versions'

/** One branch of a project (3j). Kept on `ProjectDoc.branches`, by name. */
export interface ProjectBranch {
  name: string
  /** Main, or the version it was opened from (3c). */
  from: { main: true } | { versionId: string }
  /** Every main document's heads when the branch opened, or at its last
   *  Update from main. Unwritten documents read here. */
  base: Record<string, string[]>
  createdBy: string
  createdAt: number
  /** Main's document id to the branch's copy. Filled on first write. */
  copies: Record<string, string>
  /** Documents made on the branch, added to main's catalog at merge. */
  created: string[]
  /** Their catalog titles, which the documents themselves do not carry. */
  createdTitles?: Record<string, string>
  /** Main's documents removed on the branch, removed from main at merge. */
  deleted?: string[]
  sessionIds: string[]
  /** The code branch that travels with it, when the work touches code. */
  code?: string
  status: 'open' | 'merged' | 'discarded'
  closedAt?: number
  /** A merge in progress or done (3l): which steps have landed. */
  merge?: {
    opId: string
    versionId?: string
    merged: Record<string, string>
    pr?: 'merged'
  }
  /** Picks for overlaps, by document then overlap index (3k). */
  picks?: Record<string, Record<string, Pick>>
}

async function ready<T>(repo: Repo, id: string): Promise<DocHandle<T>> {
  const h = await repo.find<T>(id as AnyDocumentId)
  await (h as { whenReady?: () => Promise<void> }).whenReady?.()
  return h
}

const plain = <T>(v: T): T =>
  v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T)

/** A project's branches. Pure. */
export function projectBranches(projectDoc: unknown): ProjectBranch[] {
  const b = (projectDoc as { branches?: unknown } | undefined)?.branches
  if (!b || typeof b !== 'object') return []
  return Object.values(plain(b) as Record<string, ProjectBranch>)
}

export function findBranch(
  projectDoc: unknown,
  name: string,
): ProjectBranch | undefined {
  return projectBranches(projectDoc).find((b) => b.name === name)
}

/** The heads of every document in the catalog now. */
async function catalogHeads(
  repo: Repo,
  projectId: string,
): Promise<Record<string, string[]>> {
  const project = await ready(repo, projectId)
  const out: Record<string, string[]> = {}
  for (const ref of projectDocuments(project.doc())) {
    try {
      const h = await ready(repo, ref.id)
      const d = h.doc()
      if (d) out[ref.id] = A.getHeads(d as A.Doc<unknown>)
    } catch {
      // Not reachable here: the branch reads it live when it arrives.
    }
  }
  return out
}

/** Open a branch from main (pinning main now) or from a saved version. */
export async function createBranch(
  repo: Repo,
  projectId: string,
  input: { name: string; by: string; fromVersion?: string },
): Promise<ProjectBranch> {
  const project = await ready<{ branches?: Record<string, ProjectBranch> }>(
    repo,
    projectId,
  )
  if (findBranch(project.doc(), input.name)?.status === 'open') {
    throw new Error(`A branch named "${input.name}" is already open.`)
  }
  let base: Record<string, string[]>
  let from: ProjectBranch['from'] = { main: true }
  if (input.fromVersion) {
    const version = (await listVersions(repo, projectId)).find(
      (v) => v.id === input.fromVersion,
    )
    if (!version) throw new Error(`No version ${input.fromVersion}.`)
    base = version.heads
    from = { versionId: version.id }
  } else {
    base = await catalogHeads(repo, projectId)
  }
  const branch: ProjectBranch = {
    name: input.name,
    from,
    base,
    createdBy: input.by,
    createdAt: Date.now(),
    copies: {},
    created: [],
    sessionIds: [],
    status: 'open',
  }
  changeProject(project, (p) => {
    if (!p.branches) p.branches = {}
    p.branches[input.name] = branch
  })
  return branch
}

/**
 * A document as the branch reads it: its copy when the branch wrote it, else
 * main at the branch's base, else main as it is (a document made after).
 */
export async function readOnBranch(
  repo: Repo,
  projectId: string,
  branchName: string,
  documentId: string,
): Promise<unknown> {
  const project = await ready(repo, projectId)
  const branch = findBranch(project.doc(), branchName)
  const copy = branch?.copies[documentId]
  if (copy) return (await ready(repo, copy)).doc()
  const main = (await ready(repo, documentId)).doc() as A.Doc<unknown>
  const heads = branch?.base[documentId]
  return heads ? A.view(main, heads) : main
}

/**
 * Make a document on a branch (3j). It is a document like any other, but main's
 * catalog does not list it until the branch merges, so main never reads it.
 */
export async function createOnBranch(
  repo: Repo,
  projectId: string,
  branchName: string,
  opts: Parameters<typeof createDocument>[2],
): Promise<string> {
  const project = await ready<{ branches?: Record<string, ProjectBranch> }>(
    repo,
    projectId,
  )
  const branch = findBranch(project.doc(), branchName)
  if (!branch || branch.status !== 'open') {
    throw new Error(`No open branch "${branchName}".`)
  }
  const handle = await createDocument(repo, projectId, {
    ...opts,
    catalog: false,
  })
  const id = handle.documentId as string
  changeProject(project, (p) => {
    const b = p.branches?.[branchName]
    if (!b) return
    b.created.push(id)
    if (!b.createdTitles) b.createdTitles = {}
    b.createdTitles[id] = opts.title ?? opts.path
  })
  return id
}

/**
 * Remove a document on a branch (3j). One made on the branch is simply
 * forgotten; one of main's is marked, and leaves main's catalog at merge.
 */
export async function deleteOnBranch(
  repo: Repo,
  projectId: string,
  branchName: string,
  documentId: string,
): Promise<void> {
  const project = await ready<{ branches?: Record<string, ProjectBranch> }>(
    repo,
    projectId,
  )
  const branch = findBranch(project.doc(), branchName)
  if (!branch || branch.status !== 'open') {
    throw new Error(`No open branch "${branchName}".`)
  }
  changeProject(project, (p) => {
    const b = p.branches?.[branchName]
    if (!b) return
    // Draft lists may hold scalars (RFC 0357 S11), which indexOf never finds.
    const i = b.created.findIndex((id) => projectString(id) === documentId)
    if (i >= 0) {
      b.created.splice(i, 1)
      if (b.createdTitles) delete b.createdTitles[documentId]
      return
    }
    if (!b.deleted) b.deleted = []
    if (!b.deleted.some((id) => projectString(id) === documentId))
      b.deleted.push(documentId)
  })
}

/**
 * What a branch lists (3j): main's catalog, less what the branch removed,
 * plus what it made. Pure over the project and the made documents' own docs.
 */
export function branchDocuments(
  projectDoc: unknown,
  branchName: string,
  made: Map<string, { path?: unknown; mime?: unknown; dataType?: unknown }>,
): ReturnType<typeof projectDocuments> {
  const branch = findBranch(projectDoc, branchName)
  const main = projectDocuments(projectDoc)
  if (!branch) return main
  const gone = new Set(branch.deleted ?? [])
  const out = main.filter((d) => !gone.has(d.id))
  for (const id of branch.created) {
    const doc = made.get(id)
    const path = doc?.path == null ? id : String(doc.path)
    out.push({
      id,
      title: branch.createdTitles?.[id] ?? path,
      path,
      mime: doc?.mime == null ? 'text/markdown' : String(doc.mime),
      updatedAt: branch.createdAt,
      ...(doc?.dataType != null ? { dataType: String(doc.dataType) } : {}),
    } as (typeof out)[number])
  }
  return out
}

/**
 * The branch's copy of a document, made on first write. Returns the copy's id.
 * Forks main at the base; on a branch from a version, copies main now and
 * restores the version's state into the copy.
 */
export async function copyOnBranch(
  repo: Repo,
  projectId: string,
  branchName: string,
  documentId: string,
): Promise<string> {
  const project = await ready<{ branches?: Record<string, ProjectBranch> }>(
    repo,
    projectId,
  )
  const branch = findBranch(project.doc(), branchName)
  if (!branch || branch.status !== 'open') {
    throw new Error(`No open branch "${branchName}".`)
  }
  const existing = branch.copies[documentId]
  if (existing) return existing
  // Made on this branch: it is already the branch's own, and main has none.
  if (branch.created.includes(documentId)) return documentId
  const main = (await ready(repo, documentId)).doc() as A.Doc<unknown>
  const heads = branch.base[documentId]
  let forked: A.Doc<unknown>
  if ('versionId' in branch.from && heads) {
    // From a version: main now, then the version's state written over it, so
    // merging back carries that restore into main.
    const then = A.view(main, heads) as {
      content?: unknown
      data?: unknown
    }
    forked = A.change(
      A.clone(main),
      { message: changeNote({ restores: heads }) },
      (d) => {
        const doc = d as { content?: unknown; data?: unknown }
        if (then.content !== undefined) {
          if (typeof doc.content !== 'string') doc.content = ''
          A.updateText(
            d as A.Doc<unknown>,
            ['content'],
            readDocumentContent(then),
          )
        }
        if (then.data !== undefined) doc.data = plain(then.data)
      },
    )
  } else {
    forked = A.clone(heads ? A.view(main, heads) : main)
  }
  const copy = repo.import(A.save(forked))
  const copyId = copy.documentId as string
  const forkedFrom = A.getHeads(main)
  changeProject(project, (p) => {
    const b = p.branches?.[branchName]
    if (b && !b.copies[documentId]) {
      b.copies[documentId] = copyId
      // From a version, the copy forked from main NOW, restore and all: that
      // is what main's later work is compared against, so the restore merges
      // as the branch's own change and not as an overlap with main.
      if ('versionId' in b.from) b.base[documentId] = forkedFrom
    }
  })
  return findBranch(project.doc(), branchName)?.copies[documentId] ?? copyId
}

/** Write text on a branch: copy on first write, then splice into the copy. */
export async function writeOnBranch(
  repo: Repo,
  projectId: string,
  branchName: string,
  documentId: string,
  content: string,
  opts: { changeMessage?: string } = {},
): Promise<string> {
  const copyId = await copyOnBranch(repo, projectId, branchName, documentId)
  const copy = await ready<{ content?: unknown; updatedAt?: number }>(
    repo,
    copyId,
  )
  copy.change(
    (d) => {
      if (typeof d.content !== 'string') d.content = ''
      A.updateText(d as A.Doc<unknown>, ['content'], content)
      d.updatedAt = Date.now()
    },
    opts.changeMessage ? { message: opts.changeMessage } : {},
  )
  return copyId
}

/** Discard: the branch is marked discarded and nothing else (3l). */
export async function discardBranch(
  repo: Repo,
  projectId: string,
  branchName: string,
): Promise<void> {
  const project = await ready<{ branches?: Record<string, ProjectBranch> }>(
    repo,
    projectId,
  )
  changeProject(project, (p) => {
    const b = p.branches?.[branchName]
    if (b && projectString(b.status) === 'open') {
      b.status = 'discarded'
      b.closedAt = Date.now()
    }
  })
}

/** One document's comparison with main (3k). */
export interface DocumentComparison {
  documentId: string
  copyId: string
  /** Paragraphs or fields changed on both sides since the copy was made. */
  overlaps: Overlap[]
  /** Every overlap has a pick. */
  resolved: boolean
  text?: { added: number; removed: number }
  fields?: string[]
  /** What the branch changed, paragraph by paragraph, since its base. */
  branchHunks: Hunk[]
  /** What main changed since the branch's base: what merging keeps. */
  mainHunks: Hunk[]
  /** The branch's record field changes, with before and after. */
  fieldChanges: FieldChange[]
}

/** Compare a branch with main. Writes nothing (3k). */
/**
 * A branch from a version reads every document at the version (3j), so merging
 * it must bring every one of them back, not only the ones somebody happened to
 * open (0313 adversarial review, finding 7). Each covered document whose state
 * now differs from the version gets its copy here, which writes the restore to
 * the version into the copy; compare then shows it and merge carries it.
 * Writes only branch copies, never main.
 */
async function materializeVersion(
  repo: Repo,
  projectId: string,
  branchName: string,
): Promise<void> {
  const project = await ready(repo, projectId)
  const branch = findBranch(project.doc(), branchName)
  if (!branch || branch.status !== 'open' || !('versionId' in branch.from))
    return
  for (const [documentId, heads] of Object.entries(branch.base)) {
    if (branch.copies[documentId] || branch.deleted?.includes(documentId))
      continue
    let differs = false
    try {
      const main = (await ready(repo, documentId)).doc() as A.Doc<unknown>
      const then = A.view(main, heads)
      differs =
        readDocumentContent(then) !== readDocumentContent(main) ||
        JSON.stringify(dataOf(then)) !== JSON.stringify(dataOf(main))
    } catch {
      continue
    }
    if (differs) await copyOnBranch(repo, projectId, branchName, documentId)
  }
}

export async function compareBranch(
  repo: Repo,
  projectId: string,
  branchName: string,
): Promise<DocumentComparison[]> {
  await materializeVersion(repo, projectId, branchName)
  const project = await ready(repo, projectId)
  const branch = findBranch(project.doc(), branchName)
  if (!branch) return []
  const out: DocumentComparison[] = []
  for (const [documentId, copyId] of Object.entries(branch.copies)) {
    const main = (await ready(repo, documentId)).doc() as A.Doc<unknown>
    const copy = (await ready(repo, copyId)).doc() as A.Doc<unknown>
    const heads = branch.base[documentId]
    const base = heads ? A.view(main, heads) : main
    const picks = branch.picks?.[documentId] ?? {}
    const baseText = readDocumentContent(base)
    const { overlaps, text } = mergeParagraphs(
      baseText,
      readDocumentContent(main),
      readDocumentContent(copy),
      picks,
    )
    const fieldOverlaps = fieldOverlapsOf(base, main, copy)
    const all = [
      ...overlaps,
      ...fieldOverlaps.map((path, i) => ({
        index: overlaps.length + i,
        base: path,
        main: path,
        branch: path,
      })),
    ]
    const copyText = readDocumentContent(copy)
    out.push({
      documentId,
      copyId,
      overlaps: all,
      resolved:
        text !== null &&
        fieldOverlaps.every(
          (_, i) => picks[String(overlaps.length + i)] !== undefined,
        ),
      text: lineDelta(baseText, copyText),
      fields: diffData(dataOf(base), dataOf(copy)).map((f) => f.path),
      branchHunks: diffParagraphs(baseText, copyText),
      mainHunks: diffParagraphs(baseText, readDocumentContent(main)),
      fieldChanges: diffData(dataOf(base), dataOf(copy)),
    })
  }
  return out
}

const dataOf = (d: unknown) =>
  plain((d as { data?: unknown } | undefined)?.data ?? undefined)

function at(obj: unknown, path: string): unknown {
  let cur = obj
  for (const k of path.split('.')) {
    if (!cur || typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[k]
  }
  return cur
}

function setPath(data: unknown, path: string, value: unknown) {
  const keys = path.split('.')
  let cur = data as Record<string, unknown> | undefined
  for (const k of keys.slice(0, -1)) {
    const next = cur?.[k]
    if (!next || typeof next !== 'object') return
    cur = next as Record<string, unknown>
  }
  if (!cur) return
  const last = keys[keys.length - 1] as string
  if (value === undefined) delete cur[last]
  else cur[last] = plain(value)
}

function fieldOverlapsOf(
  base: unknown,
  main: unknown,
  copy: unknown,
): string[] {
  const mine = new Set(diffData(dataOf(base), dataOf(main)).map((f) => f.path))
  return diffData(dataOf(base), dataOf(copy))
    .map((f) => f.path)
    .filter((p) => mine.has(p))
}

function lineDelta(before: string, after: string) {
  const a = new Set(before.split('\n'))
  const b = new Set(after.split('\n'))
  return {
    added: [...b].filter((l) => !a.has(l)).length,
    removed: [...a].filter((l) => !b.has(l)).length,
  }
}

/** Pick a side for one overlap, written on the branch (3k). */
export async function pickOverlap(
  repo: Repo,
  projectId: string,
  branchName: string,
  documentId: string,
  index: number,
  pick: Pick,
): Promise<void> {
  const project = await ready<{ branches?: Record<string, ProjectBranch> }>(
    repo,
    projectId,
  )
  changeProject(project, (p) => {
    const b = p.branches?.[branchName]
    if (!b) return
    if (!b.picks) b.picks = {}
    if (!b.picks[documentId]) b.picks[documentId] = {}
    ;(b.picks[documentId] as Record<string, Pick>)[String(index)] = pick
  })
}

export type MergeResult =
  | { ok: true; merged: number; versionId: string }
  | { ok: false; reason: 'not-open' }
  | { ok: false; reason: 'overlaps'; documents: string[] }

/**
 * Merge a branch into main (3l). Order: save a version of main, merge each
 * copy, then mark merged. Each step checks before acting, so a retry after an
 * interruption resumes instead of repeating, and `merge` on the branch says how
 * far it got. A pull request is the caller's (the code half): this is
 * documents and app records.
 */
export async function mergeBranch(
  repo: Repo,
  projectId: string,
  branchName: string,
  opts: { by: string } = { by: 'user' },
): Promise<MergeResult> {
  const project = await ready<{ branches?: Record<string, ProjectBranch> }>(
    repo,
    projectId,
  )
  const open = findBranch(project.doc(), branchName)
  if (!open || open.status !== 'open') return { ok: false, reason: 'not-open' }

  const blocked = (await compareBranch(repo, projectId, branchName)).filter(
    (c) => !c.resolved,
  )
  // Read again: comparing a branch from a version makes the copies its merge
  // must carry (finding 7), so the branch as read above is out of date.
  const branch = findBranch(project.doc(), branchName) ?? open
  if (blocked.length) {
    return {
      ok: false,
      reason: 'overlaps',
      documents: blocked.map((c) => c.documentId),
    }
  }

  const opId = branch.merge?.opId ?? `merge:${branchName}:${branch.createdAt}`
  const version = await saveVersion(repo, projectId, {
    name: `Before merging ${branchName}`,
    by: 'auto',
    covers: { kind: 'project' },
    opId,
  })
  changeProject(project, (p) => {
    const b = p.branches?.[branchName]
    if (b && !b.merge) b.merge = { opId, versionId: version.id, merged: {} }
  })

  let merged = 0
  for (const [documentId, copyId] of Object.entries(branch.copies)) {
    const done = findBranch(project.doc(), branchName)?.merge?.merged[
      documentId
    ]
    if (done) {
      merged++
      continue
    }
    const main = await ready<Record<string, unknown>>(repo, documentId)
    const copy = await ready<Record<string, unknown>>(repo, copyId)
    const heads = branch.base[documentId]
    const mainDoc = main.doc() as A.Doc<unknown>
    const base = heads ? A.view(mainDoc, heads) : mainDoc
    const picks = branch.picks?.[documentId] ?? {}
    const textMerge = mergeParagraphs(
      readDocumentContent(base),
      readDocumentContent(mainDoc),
      readDocumentContent(copy.doc()),
      picks,
    )
    const resolved =
      typeof (mainDoc as { content?: unknown }).content === 'string'
        ? textMerge.text
        : null
    // A field both sides set merges last-writer-wins; the pick decides it.
    const fieldPicks = fieldOverlapsOf(base, mainDoc, copy.doc()).map(
      (path, i) => ({
        path,
        value: at(
          picks[String(textMerge.overlaps.length + i)] === 'main'
            ? dataOf(mainDoc)
            : dataOf(copy.doc()),
          path,
        ),
      }),
    )
    ;(main as unknown as { merge: (h: unknown) => void }).merge(copy)
    if (fieldPicks.length) {
      main.change(
        (d) => {
          for (const f of fieldPicks) setPath(d.data, f.path, f.value)
        },
        { message: changeNote({ merges: branchName, authorId: opts.by }) },
      )
    }
    // Where both sides rewrote a paragraph, Automerge kept both interleaved.
    // Write the picked result over it, as one change stamped with the branch.
    if (resolved !== null && readDocumentContent(main.doc()) !== resolved) {
      main.change(
        (d) => {
          if (typeof d.content !== 'string') d.content = ''
          A.updateText(d as A.Doc<unknown>, ['content'], resolved)
        },
        { message: changeNote({ merges: branchName, authorId: opts.by }) },
      )
    }
    const now = Date.now()
    await touchCatalog(repo, projectId, documentId, now)
    const after = A.getHeads(main.doc() as A.Doc<unknown>)
    changeProject(project, (p) => {
      const b = p.branches?.[branchName]
      if (b?.merge) b.merge.merged[documentId] = after[0] ?? ''
    })
    merged++
  }
  // What the branch made joins main's catalog, and what it removed leaves it.
  // Both are idempotent, so a merge resumed after a crash repeats them safely.
  for (const id of branch.created) {
    const doc = (await ready<Record<string, unknown>>(repo, id)).doc() ?? {}
    const path = doc.path == null ? id : String(doc.path)
    await catalogDocument(repo, projectId, id, {
      title: branch.createdTitles?.[id] ?? path,
      path,
      mime: doc.mime == null ? 'text/markdown' : String(doc.mime),
      updatedAt: Date.now(),
      ...(doc.dataType != null ? { dataType: String(doc.dataType) } : {}),
    })
    merged++
  }
  for (const id of branch.deleted ?? []) {
    await deleteDocument(repo, projectId, id)
  }
  changeProject(project, (p) => {
    const b = p.branches?.[branchName]
    if (b) {
      b.status = 'merged'
      b.closedAt = Date.now()
    }
  })
  return { ok: true, merged, versionId: version.id }
}

/**
 * Bring main's newer work into an open branch (3j): merge main into each copy,
 * and move the base to main's heads now, so unwritten documents read the new
 * main too.
 */
export type UpdateResult =
  | { ok: true }
  | { ok: false; reason: 'not-open' }
  /** Overlaps nobody has picked yet: pick them first. */
  | { ok: false; reason: 'overlaps'; documents: string[] }

/**
 * Bring main's newer work into an open branch (3j), and move its base to main
 * now.
 *
 * NOT WHILE AN OVERLAP IS OPEN. An overlap is defined against the base; moving
 * the base past an unpicked one made it disappear, and the interleaved text
 * read as an ordinary branch edit that Merge then allowed (0313 adversarial
 * review, finding 4). So this refuses while any overlap is unpicked, and where
 * one was picked it writes the picked result into the copy before the base
 * moves, so the pick survives the update. Picks are cleared afterwards: they
 * were answers about the old base.
 */
export async function updateFromMain(
  repo: Repo,
  projectId: string,
  branchName: string,
): Promise<UpdateResult> {
  const project = await ready<{ branches?: Record<string, ProjectBranch> }>(
    repo,
    projectId,
  )
  const branch = findBranch(project.doc(), branchName)
  if (!branch || branch.status !== 'open')
    return { ok: false, reason: 'not-open' }
  const blocked = (await compareBranch(repo, projectId, branchName)).filter(
    (c) => !c.resolved,
  )
  if (blocked.length) {
    return {
      ok: false,
      reason: 'overlaps',
      documents: blocked.map((c) => c.documentId),
    }
  }
  for (const [documentId, copyId] of Object.entries(branch.copies)) {
    const main = await ready<Record<string, unknown>>(repo, documentId)
    const copy = await ready<Record<string, unknown>>(repo, copyId)
    const mainDoc = main.doc() as A.Doc<unknown>
    const heads = branch.base[documentId]
    const base = heads ? A.view(mainDoc, heads) : mainDoc
    const picks = branch.picks?.[documentId] ?? {}
    // The result the picks chose, worked out against the OLD base.
    const textMerge = mergeParagraphs(
      readDocumentContent(base),
      readDocumentContent(mainDoc),
      readDocumentContent(copy.doc()),
      picks,
    )
    const fieldPicks = fieldOverlapsOf(base, mainDoc, copy.doc()).map(
      (path, i) => ({
        path,
        value: at(
          picks[String(textMerge.overlaps.length + i)] === 'main'
            ? dataOf(mainDoc)
            : dataOf(copy.doc()),
          path,
        ),
      }),
    )
    ;(copy as unknown as { merge: (h: unknown) => void }).merge(main)
    const resolved =
      typeof (mainDoc as { content?: unknown }).content === 'string'
        ? textMerge.text
        : null
    if (
      fieldPicks.length ||
      (resolved !== null && readDocumentContent(copy.doc()) !== resolved)
    ) {
      copy.change(
        (d) => {
          for (const f of fieldPicks) setPath(d.data, f.path, f.value)
          if (resolved !== null) {
            if (typeof d.content !== 'string') d.content = ''
            A.updateText(d as A.Doc<unknown>, ['content'], resolved)
          }
        },
        { message: changeNote({ merges: 'main' }) },
      )
    }
  }
  const base = await catalogHeads(repo, projectId)
  changeProject(project, (p) => {
    const b = p.branches?.[branchName]
    if (!b) return
    b.base = base
    if (b.picks) delete b.picks
  })
  return { ok: true }
}
